import { config } from "./config";
import { chartsToCsv } from "./chartParser";
import {
  buildComparisonCharts,
  buildDualAxisChart,
  buildScatterChart,
  buildQuantileForecastChart,
  SCATTER_SOURCE,
  type ComparisonOptions,
  type DualAxisOptions,
  type ScatterOptions,
  type QuantileForecastOptions
} from "./localApi";
import type { ChartConfig, PointMap } from "./industryParser";
import type { ChartSpec, ReportDetail, ReportSummary } from "./types";

// Shared shape implemented by both the real ApiClient and the local mock
// (LocalApiClient), so the UI can use either interchangeably.
export interface ReportPage {
  items: ReportSummary[];
  // Opaque cursor for the next page; undefined when there are no more reports.
  nextCursor?: string;
}

export interface ListReportsOptions {
  includeDrafts?: boolean;
  cursor?: string;
}

export interface IndustrySummary {
  industry_id: string;
  name: string;
  category?: string;
  charts: number;
  weeks: number;
  updated_at: string;
  last_week?: string;
  report_id?: string;
}

export interface IndustryIngest {
  industryId?: string; // omit to create a new industry (id derived from name)
  name: string;
  category?: string;
  charts: ChartConfig[];
  points: PointMap;
}

export interface ClusterItem {
  industry_id: string;
  chart_id: string;
}

export interface ClusterSummary {
  cluster_id: string;
  name: string;
  count: number;
  updated_at: string;
}

export interface ClusterData extends ClusterSummary {
  items: ClusterItem[];
}

export interface ResolvedClusterChart {
  industry_id: string;
  industry_name: string;
  chart: ChartSpec;
}

export interface ResolvedCluster {
  cluster_id: string;
  name: string;
  items: ClusterItem[];
  charts: ResolvedClusterChart[];
}

export interface Api {
  listReports(options?: ListReportsOptions): Promise<ReportPage>;
  getReport(reportId: string): Promise<ReportDetail>;
  getDownloadUrl(reportId: string): Promise<string>;
  // Admin tools: build charts in the browser (shared builders) and publish.
  createComparison(files: File[], options?: ComparisonOptions): Promise<void>;
  createDualAxis(files: File[], options?: DualAxisOptions): Promise<void>;
  createQuantileForecast(files: File[], options?: QuantileForecastOptions): Promise<void>;
  // Industry scatter (admin picks X/Y numeric columns, colour=category).
  // Overwrites the same-titled chart, so re-uploading refreshes that chart.
  createScatter(files: File[], options?: ScatterOptions): Promise<void>;
  deleteReport(reportId: string): Promise<void>;
  // Edit a published report's display fields (title + category + chart
  // labels/axes) and re-publish in place — no raw re-upload.
  updateReport(reportId: string, payload: { title: string; charts: ChartSpec[]; category?: string }): Promise<void>;
  // Industry datasets: accumulate weekly time-series and auto-regenerate a report.
  listIndustries(): Promise<IndustrySummary[]>;
  ingestIndustry(input: IndustryIngest): Promise<IndustrySummary>;
  updateIndustry(industryId: string, patch: { name?: string; category?: string }): Promise<IndustrySummary>;
  deleteIndustry(industryId: string): Promise<void>;
  // Category order (drag-reorder; dashboards honour it) + bulk delete.
  getCategoryOrder(): Promise<string[]>;
  setCategoryOrder(order: string[]): Promise<string[]>;
  deleteCategory(name: string): Promise<number>;
  // Chart clusters: admin-curated cross-industry chart collections (references only).
  listClusters(): Promise<ClusterSummary[]>;
  getCluster(clusterId: string): Promise<ClusterData>;
  getClusterResolved(clusterId: string): Promise<ResolvedCluster>;
  createCluster(name: string): Promise<ClusterData>;
  updateCluster(clusterId: string, patch: { name?: string; items?: ClusterItem[] }): Promise<ClusterData>;
  addClusterItem(clusterId: string, item: ClusterItem): Promise<ClusterData>;
  deleteCluster(clusterId: string): Promise<void>;
  reorderClusters(orderedIds: string[]): Promise<ClusterSummary[]>;
  // Optional: only the local mock persists ordering today.
  reorderReports?(orderedIds: string[]): Promise<void>;
}

export class ApiClient implements Api {
  // Receives a token provider rather than a static token so every request can
  // use a freshly refreshed ID token. The provider rejects when the session is
  // gone, letting the UI route back to the sign-in screen.
  constructor(private readonly getToken: () => Promise<string>) {}

  async listReports(options: ListReportsOptions = {}): Promise<ReportPage> {
    const params = new URLSearchParams();
    if (options.includeDrafts) params.set("includeDrafts", "true");
    if (options.cursor) params.set("cursor", options.cursor);
    const query = params.toString();
    const response = await this.request<{ items: ReportSummary[]; next_cursor?: string | null }>(
      `/reports${query ? `?${query}` : ""}`
    );
    return { items: response.items ?? [], nextCursor: response.next_cursor ?? undefined };
  }

  async getReport(reportId: string): Promise<ReportDetail> {
    return this.request<ReportDetail>(`/reports/${encodeURIComponent(reportId)}`);
  }

  async getDownloadUrl(reportId: string): Promise<string> {
    const response = await this.request<{ download_url: string }>(`/reports/${encodeURIComponent(reportId)}/download`);
    return response.download_url;
  }

  // ----- Admin tools: build charts in the browser, then persist server-side.
  // The same builders power the local mock, so the produced report is identical
  // to what was validated locally; only persistence (S3 + DynamoDB) differs.

  async createComparison(files: File[], options: ComparisonOptions = {}): Promise<void> {
    const { charts, title } = await buildComparisonCharts(files, options);
    await this.publishReport(title, "시계열 종목비교", charts, options.category);
  }

  async createDualAxis(files: File[], options: DualAxisOptions = {}): Promise<void> {
    const { chart, title } = await buildDualAxisChart(files, options);
    await this.publishReport(title, "이중축 시계열 비교", [chart], options.category);
  }

  async createQuantileForecast(files: File[], options: QuantileForecastOptions = {}): Promise<void> {
    const { chart, title } = await buildQuantileForecastChart(files, options);
    await this.publishReport(title, "과거 분위 미래 예측", [chart], options.category);
  }

  // Publishes the new scatter, then deletes older charts with the SAME title so
  // re-uploading refreshes just that chart (monthly overwrite). Different titles
  // (e.g. momentum vs crowding) keep their own slots. Cleanup is best-effort.
  async createScatter(files: File[], options: ScatterOptions = {}): Promise<void> {
    const { chart, title } = await buildScatterChart(files[0], options);
    await this.publishReport(title, SCATTER_SOURCE, [chart], options.category);
    try {
      const { items } = await this.listReports({ includeDrafts: true });
      const olds = items
        .filter((r) => r.source === SCATTER_SOURCE && r.title === title)
        .sort((a, b) => b.created_at.localeCompare(a.created_at))
        .slice(1);
      await Promise.all(olds.map((r) => this.deleteReport(r.report_id)));
    } catch {
      /* best-effort cleanup; the new report is already published */
    }
  }

  async deleteReport(reportId: string): Promise<void> {
    await this.request<void>(`/reports/${encodeURIComponent(reportId)}`, { method: "DELETE" });
  }

  async updateReport(
    reportId: string,
    payload: { title: string; charts: ChartSpec[]; category?: string }
  ): Promise<void> {
    await this.request<{ report: ReportSummary }>(`/reports/${encodeURIComponent(reportId)}`, {
      method: "PATCH",
      body: JSON.stringify({
        title: payload.title,
        category: payload.category,
        charts: payload.charts,
        csv: chartsToCsv(payload.charts)
      })
    });
  }

  async listIndustries(): Promise<IndustrySummary[]> {
    return this.request<IndustrySummary[]>("/industries");
  }

  async ingestIndustry(input: IndustryIngest): Promise<IndustrySummary> {
    const body = JSON.stringify({
      name: input.name,
      category: input.category,
      charts: input.charts,
      points: input.points
    });
    const path = input.industryId
      ? `/industries/${encodeURIComponent(input.industryId)}/data`
      : "/industries";
    return this.request<IndustrySummary>(path, { method: "POST", body });
  }

  async updateIndustry(
    industryId: string,
    patch: { name?: string; category?: string }
  ): Promise<IndustrySummary> {
    return this.request<IndustrySummary>(`/industries/${encodeURIComponent(industryId)}`, {
      method: "PATCH",
      body: JSON.stringify(patch)
    });
  }

  async deleteIndustry(industryId: string): Promise<void> {
    await this.request<void>(`/industries/${encodeURIComponent(industryId)}`, { method: "DELETE" });
  }

  async getCategoryOrder(): Promise<string[]> {
    return this.request<string[]>("/category-order");
  }

  async setCategoryOrder(order: string[]): Promise<string[]> {
    return this.request<string[]>("/category-order", { method: "POST", body: JSON.stringify({ order }) });
  }

  async deleteCategory(name: string): Promise<number> {
    const res = await this.request<{ deleted: number }>(`/categories/${encodeURIComponent(name)}`, { method: "DELETE" });
    return res.deleted;
  }

  async listClusters(): Promise<ClusterSummary[]> {
    return this.request<ClusterSummary[]>("/clusters");
  }

  async getCluster(clusterId: string): Promise<ClusterData> {
    return this.request<ClusterData>(`/clusters/${encodeURIComponent(clusterId)}`);
  }

  async getClusterResolved(clusterId: string): Promise<ResolvedCluster> {
    return this.request<ResolvedCluster>(`/clusters/${encodeURIComponent(clusterId)}/resolved`);
  }

  async createCluster(name: string): Promise<ClusterData> {
    return this.request<ClusterData>("/clusters", { method: "POST", body: JSON.stringify({ name }) });
  }

  async updateCluster(clusterId: string, patch: { name?: string; items?: ClusterItem[] }): Promise<ClusterData> {
    return this.request<ClusterData>(`/clusters/${encodeURIComponent(clusterId)}`, {
      method: "PATCH",
      body: JSON.stringify(patch)
    });
  }

  async addClusterItem(clusterId: string, item: ClusterItem): Promise<ClusterData> {
    return this.request<ClusterData>(`/clusters/${encodeURIComponent(clusterId)}/items`, {
      method: "POST",
      body: JSON.stringify(item)
    });
  }

  async deleteCluster(clusterId: string): Promise<void> {
    await this.request<void>(`/clusters/${encodeURIComponent(clusterId)}`, { method: "DELETE" });
  }

  async reorderClusters(orderedIds: string[]): Promise<ClusterSummary[]> {
    return this.request<ClusterSummary[]>("/clusters/order", { method: "POST", body: JSON.stringify({ order: orderedIds }) });
  }

  private async publishReport(title: string, source: string, charts: ChartSpec[], category?: string): Promise<void> {
    await this.request<{ report: ReportSummary }>("/reports", {
      method: "POST",
      body: JSON.stringify({ title, source, category: category?.trim() || undefined, charts, csv: chartsToCsv(charts) })
    });
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    if (!config.apiBaseUrl) {
      throw new Error("API URL is not configured yet. Deploy the backend stack and set GitHub variable VITE_API_BASE_URL.");
    }

    const token = await this.getToken();
    const response = await fetch(`${config.apiBaseUrl}${path}`, {
      ...init,
      headers: {
        "content-type": "application/json",
        authorization: token,
        ...init.headers
      }
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Request failed with ${response.status}`);
    }

    return response.json() as Promise<T>;
  }
}
